import { Component, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { User, UserAddress } from 'app/core/models/user.model';
import {
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  tap,
  finalize,
} from 'rxjs';
import {
  AddressType,
  AddressTypeLabel,
  parseAddressType,
} from '../../enums/address-type.enum';
import { CouponApiService } from '../../services/coupon-api.service';
import { CouponFormsService } from '../../services/coupon-forms.service';
import { GoogleGeocodingService } from 'app/core/services/google-geocoding.service';
import { ToastService } from 'app/core/services/toast.service';
import { UserCoupon } from '../../dtos/user-coupon.dto';

interface UserSearchResult {
  user: User | null;
  loading: boolean;
}

interface AddressOption {
  key: string;
  name: string;
  originalData: UserAddress;
  formIndex: number;
}

@Component({
  selector: 'app-coupon-search-user',
  templateUrl: './coupon-search-user.component.html',
  styleUrls: ['./coupon-search-user.component.scss'],
})
export class CouponSearchUserComponent implements OnInit {
  formUser!: typeof this.formsService.formUser;
  formAddress!: typeof this.formsService.formAddress;
  formUserAddresses!: FormArray;
  editMode = false;
  previousEmailValue = '';
  previousPhoneValue = '';

  addressModalVisible = false;
  currentAddressType: AddressType = AddressType.HOME;
  addressModalTitle = 'Adicionar endereço';
  editingAddressId: string | null = null;
  addressForm!: FormGroup;

  confirmModalVisible = false;
  addressToDeleteId: string | null = null;

  userSearchResult$!: Observable<UserSearchResult>;
  addressOptions: AddressOption[] = [];

  readonly addressType = AddressType;
  readonly addressTypeLabel = AddressTypeLabel;

  isHomeAddress(): boolean {
    return this.currentAddressType === AddressType.HOME;
  }

  isWorkAddress(): boolean {
    return this.currentAddressType === AddressType.WORK;
  }

  isOtherAddress(): boolean {
    return this.currentAddressType === AddressType.OTHER;
  }

  isCepLoading = false;

  constructor(
    private formsService: CouponFormsService,
    private couponApiService: CouponApiService,
    private router: Router,
    private googleGeocoding: GoogleGeocodingService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.formUser = this.formsService.formUser;
    this.formAddress = this.formsService.formAddress;
    this.formUserAddresses = this.formsService.formUserAddresses;
  }

  private markDirtyTouchedAndRevalidate(controls: string[]) {
    controls.forEach(controlName => {
      const control = this.addressForm.get(controlName);
      control?.markAsDirty({ onlySelf: true });
      control?.markAsTouched({ onlySelf: true });
    });
    this.addressForm.updateValueAndValidity({ emitEvent: false });
  }

  onSubmitUser() {
    if (this.formUser.get('cpf')?.invalid) {
      this.formUser.markAllAsTouched();
      return;
    }

    const cpf = this.formUser.value.cpf!;

    this.editMode = false;

    this.userSearchResult$ = this.couponApiService.findUserByCPF(cpf).pipe(
      map(res => ({
        user: res?.response || null,
        loading: false,
      })),
      catchError(() => of({ user: null, loading: false })),
      startWith({ user: null, loading: true }),
      tap(result => {
        if (result.user) {
          this.fillUserForm(result.user);
          this.formsService.setUserAddresses(result.user.address || []);
          this.updateAddressOptions();

          if (this.addressOptions.length > 0) {
            this.selectAddress(this.addressOptions[0].originalData);
          }
        } else {
          this.addressOptions = [];
          this.formsService.setUserAddresses([]);
        }
      }),
      shareReplay(1),
    );
  }

  fillUserForm(user: User) {
    this.formUser.patchValue({
      id: user.id,
      name: user.name,
      cpf: user.cpf,
      birthDate: user.birthDate,
      email: user.email,
      phone: user.phone,
      pushToken: user.pushToken,
      hasAppointmentCandidate: user.hasAppointmentCandidate,
      couponProcedures: user.couponProcedures as UserCoupon[],
      roles: user.roles,
      s4eUserId: user.s4eUserId,
    });
  }

  selectAddress(addr: UserAddress) {
    this.formAddress.patchValue({
      id: addr.id,
      title: addr.title,
      zipCode: addr.zipCode,
      state: addr.state,
      city: addr.city,
      neighborhood: addr.neighborhood,
      street: addr.street,
      number: addr.number,
      complement: addr.complement,
      reference: addr.reference,
      latitude: String(addr.latitude ?? ''),
      longitude: String(addr.longitude ?? ''),
      selectedAddress: addr,
    });
  }

  onGoNext() {
    this.router.navigate(['/coupon/physician-list']);
  }

  handleEditMode() {
    if (this.editMode) {
      this.formUser.patchValue({
        email: this.previousEmailValue,
        phone: this.previousPhoneValue,
      });
    } else {
      this.previousEmailValue = this.formUser.get('email')?.value || '';
      this.previousPhoneValue = this.formUser.get('phone')?.value || '';
    }
    this.editMode = !this.editMode;
  }

  saveChanges() {
    this.editMode = false;

    if (this.formAddress.value.selectedAddress) {
      const selectedAddress = this.formAddress.value.selectedAddress;
      this.selectAddress(selectedAddress);
    }
  }

  openAddressModal(address?: UserAddress) {
    this.addressModalVisible = true;

    if (address) {
      this.addressModalTitle = 'Editar endereço';
      this.editingAddressId = address.id;
      this.addressForm = this.formsService.createAddressFormGroup(address);
      this.currentAddressType = parseAddressType(address.title);
    } else {
      this.addressModalTitle = 'Adicionar endereço';
      this.editingAddressId = null;
      this.addressForm = this.formsService.createAddressFormGroup();
      this.currentAddressType = AddressType.HOME;
      this.addressForm.patchValue({
        title: AddressTypeLabel[AddressType.HOME],
        latitude: 0,
        longitude: 0,
      });
    }
  }

  onAddressTypeChange(type: AddressType) {
    this.currentAddressType = type;

    this.addressForm.patchValue({
      title: AddressTypeLabel[type],
    });
  }

  closeAddressModal(): void {
    this.addressModalVisible = false;
  }

  saveAddress(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    const addressData: UserAddress = this.addressForm.value;

    if (this.editingAddressId) {
      const foundAddress = this.formsService.findUserAddressById(
        this.editingAddressId,
      );
      if (foundAddress) {
        this.formsService.updateUserAddress(foundAddress.index, addressData);

        const currentSelectedId =
          this.formAddress.value.selectedAddress?.id ||
          this.formAddress.value.id;
        if (currentSelectedId === this.editingAddressId) {
          this.selectAddress(addressData);
        }
      }
    } else {
      addressData.id = crypto.randomUUID();
      this.formsService.addUserAddress(addressData);
    }

    this.updateAddressOptions();
    this.addressModalVisible = false;
  }

  deleteAddress(addressId: string) {
    const foundAddress = this.formsService.findUserAddressById(addressId);
    if (foundAddress) {
      this.formsService.removeUserAddress(foundAddress.index);
      this.updateAddressOptions();
      this.addressModalVisible = false;
    }
  }

  updateAddressOptions() {
    this.addressOptions = [];
    const addressesArray = this.formsService.getUserAddressesAsArray();

    this.addressOptions = addressesArray.map((addr, idx) => ({
      key: 'addr_' + idx,
      name: `${addr.street}, ${addr.number} - ${addr.city}/${addr.state}`,
      originalData: addr,
      formIndex: idx,
    }));
  }

  addNewAddress() {
    this.openAddressModal();
  }

  confirmDeleteAddress(address?: UserAddress) {
    if (address) {
      this.addressToDeleteId = address.id;
    } else if (this.editingAddressId) {
      this.addressToDeleteId = this.editingAddressId;
    } else {
      return;
    }

    this.confirmModalVisible = true;
  }

  cancelDeleteAddress() {
    this.confirmModalVisible = false;
    this.addressToDeleteId = null;
  }

  executeDeleteAddress() {
    if (this.addressToDeleteId) {
      const foundAddress = this.formsService.findUserAddressById(
        this.addressToDeleteId,
      );
      if (foundAddress) {
        this.formsService.removeUserAddress(foundAddress.index);
        this.updateAddressOptions();
        this.confirmModalVisible = false;
        this.addressToDeleteId = null;
        this.addressModalVisible = false;
      }
    }
  }

  private searchCep(cleanCep: string): void {
    this.isCepLoading = true;

    this.googleGeocoding
      .geocodeByCep(cleanCep)
      .pipe(finalize(() => (this.isCepLoading = false)))
      .subscribe(result => {
        if (!result) {
          this.toast.showWarnToast(
            'CEP não encontrado',
            'Confira e tente novamente.',
          );
          return;
        }

        const normalizedZip = (result.zipCode ?? cleanCep).replace(/\D/g, '');

        this.addressForm.patchValue(
          {
            zipCode: normalizedZip,
            state: result.state,
            city: result.city,
            neighborhood: result.neighborhood,
            street: result.street,
            latitude: result.latitude ?? this.addressForm.value.latitude,
            longitude: result.longitude ?? this.addressForm.value.longitude,
          },
          { emitEvent: false },
        );

        // garante que o form saia do estado pristine/untouched e recalcule a validade
        this.markDirtyTouchedAndRevalidate([
          'zipCode',
          'state',
          'city',
          'neighborhood',
          'street',
          'latitude',
          'longitude',
        ]);
      });
  }

  onSearchCep(): void {
    const cep = this.addressForm.get('zipCode')?.value || '';
    const clean = (cep || '').replace(/\D/g, '');
    if (clean.length !== 8) {
      this.toast.showWarnToast('CEP inválido', 'Informe 8 dígitos.');
      return;
    }
    this.searchCep(clean);
  }
}
